import { create } from "zustand";

import type { Phase } from "../../api/types";

/** Transition animée d'un temps à l'autre (T1, T2, T3), comme l'iPad (CartoView.startSmoothTransition) : les
 *  unités glissent de leur position du temps quitté à celle du temps choisi, leurs symboles de mission avec elles
 *  (position et orientation par le plus court chemin) ; 2,5 s, courbe douce au départ et à l'arrivée. Une horloge
 *  unique pour les calques ; chacun garde la position qu'il affiche, d'où part la transition suivante — un temps
 *  choisi en pleine transition repart de là, sans saut. Mouvement réduit demandé par le poste : pas de transition.
 *  Demande d'Alexis du 01/10/2026 (exception à la carte figée). */

export const PHASE_TRANSITION_MS = 2500; // CartoView.animationDuration de l'iPad

/** Réponse du serveur qui change le dispositif sans changer de temps (mission, fuseau, ancrage, rôles,
 *  annulation) : unités et symboles glissent de ce qu'ils affichent vers la réponse (docs/14 §2.10). */
export const DATA_TRANSITION_MS = 900;

/** Fondu d'ouverture du dossier, même durée que `REVEAL_MS` (map/reveal.ts, demande du 28/09/2026). */
export const ENTITY_REVEAL_MS = 900;

/** Sortie d'une entité de la carte (unité masquée ou retirée, symbole remplacé) : gardée à opacité décroissante
 *  pendant ce temps avant de quitter la collection (docs/14 §2.10). */
export const MAP_EXIT_MS = 400;

interface PhaseClock {
  /** Numéro de la transition en cours (chaque changement de temps, ou de données, en ouvre une). */
  id: number;
  /** Temps quitté, pour placer un symbole qui n'était pas affiché ; pour une transition de données, le temps
   *  courant si l'appelant l'a donné (docs/14 §2.10), sinon `null`. */
  from: Phase | null;
  /** Avancement brut de 0 à 1 ; 1 hors transition. */
  progress: number;
  /** Nature de la transition : changement de temps (2,5 s) ou nouvelles données à temps égal (900 ms). */
  kind: "phase" | "data";
  /** Durée de la transition en cours (ms). */
  duration: number;
  /** Facteur d'ouverture du dossier, de 0 à 1 (courbe douce appliquée), multiplié dans l'opacité de chaque
   *  entité des couches qui ne passent pas par `revealPaint` ; 0 tant qu'aucun dossier n'est ouvert. */
  reveal: number;
}

export const usePhaseClock = create<PhaseClock>(() => ({
  id: 0,
  from: null,
  progress: 1,
  kind: "phase",
  duration: PHASE_TRANSITION_MS,
  reveal: 0,
}));

/** Une seule boucle `requestAnimationFrame` porte la transition et le fondu d'ouverture. */
let frame = 0;
let transition: { started: number; duration: number } | null = null;
let fade: { started: number; duration: number; from: number; to: number } | null = null;
let revealTarget: boolean | null = null;

function reducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

function animated(): boolean {
  return !reducedMotion() && typeof requestAnimationFrame === "function";
}

function tick(now: number): void {
  frame = 0;
  const patch: Partial<PhaseClock> = {};
  if (transition) {
    const progress = Math.max(0, Math.min(1, (now - transition.started) / transition.duration));
    patch.progress = progress;
    if (progress >= 1) transition = null;
  }
  if (fade) {
    const raw = Math.max(0, Math.min(1, (now - fade.started) / fade.duration));
    patch.reveal = lerp(fade.from, fade.to, smoothstep(raw));
    if (raw >= 1) fade = null;
  }
  if (Object.keys(patch).length) usePhaseClock.setState(patch);
  if (transition || fade) frame = requestAnimationFrame(tick);
}

function run(): void {
  if (frame) cancelAnimationFrame(frame);
  frame = requestAnimationFrame(tick);
}

/** Ouvre une transition : chaque calque repart de ce qu'il affiche (il le relève au changement de `id`). */
function open(kind: PhaseClock["kind"], from: Phase | null, duration: number): void {
  const id = usePhaseClock.getState().id + 1;
  if (!animated()) {
    transition = null;
    usePhaseClock.setState({ id, from, kind, duration, progress: 1 });
    return;
  }
  transition = { started: performance.now(), duration };
  usePhaseClock.setState({ id, from, kind, duration, progress: 0 });
  run();
}

/** Ouvre une transition depuis le temps `from` (appelé au changement de temps). */
export function startPhaseTransition(from: Phase): void {
  open("phase", from, PHASE_TRANSITION_MS);
}

/** Ouvre une transition de données, sur la même horloge : à la réponse du serveur, unités et symboles partent de
 *  la position qu'ils affichent (une transition en cours est reprise de là, sans saut) vers les nouvelles.
 *  `from` : le temps courant (§2.10, `useUi.getState().phase` chez l'appelant ; pas d'import de l'état ici,
 *  state/ui.ts important déjà ce module). */
export function startDataTransition(durationMs = DATA_TRANSITION_MS, from: Phase | null = null): void {
  open("data", from, Math.max(1, durationMs));
}

/** Fondu d'ouverture des entités : vers 1 à l'ouverture du dossier, vers 0 à sa fermeture ; sans effet si l'état
 *  demandé est déjà celui en cours. Le premier appel « fermé » se place directement à 0. */
export function syncReveal(shown: boolean, durationMs = ENTITY_REVEAL_MS): void {
  if (revealTarget === shown) return;
  const first = revealTarget === null;
  revealTarget = shown;
  const to = shown ? 1 : 0;
  if ((first && !shown) || !animated()) {
    fade = null;
    usePhaseClock.setState({ reveal: to });
    return;
  }
  fade = { started: performance.now(), duration: Math.max(1, durationMs), from: usePhaseClock.getState().reveal, to };
  run();
}

/** Devenir d'une entité pendant une transition : gardée, qui apparaît, qui s'efface. */
export type EntityFate = "kept" | "enter" | "exit";

/** Opacité d'une entité (`icon-opacity: ["get", "opacity"]`) à l'avancement adouci `eased` : fondu propre
 *  (entrée de 0 à 1, sortie de 1 à 0, gardée à 1), parti de l'opacité affichée au départ (`start`, pour une
 *  transition reprise en cours de route), multiplié par le facteur d'ouverture du dossier `reveal`. */
export function entityOpacity(fate: EntityFate, eased: number, reveal = 1, start?: number): number {
  const t = Math.min(1, Math.max(0, eased));
  const from = start ?? (fate === "enter" ? 0 : 1);
  const to = fate === "exit" ? 0 : 1;
  return Math.min(1, Math.max(0, lerp(from, to, t) * reveal));
}

/** Avancement de la sortie d'une entité partie à `leftAt` (horloge `performance.now()`), de 0 à 1 en
 *  `MAP_EXIT_MS` : au-delà, l'entité quitte la collection. */
export function exitProgress(leftAt: number, now: number, durationMs = MAP_EXIT_MS): number {
  return Math.min(1, Math.max(0, (now - leftAt) / Math.max(1, durationMs)));
}

/** Remet l'horloge à son état initial (tests). */
export function resetPhaseClockForTests(): void {
  if (frame && typeof cancelAnimationFrame === "function") cancelAnimationFrame(frame);
  frame = 0;
  transition = null;
  fade = null;
  revealTarget = null;
  usePhaseClock.setState({ id: 0, from: null, progress: 1, kind: "phase", duration: PHASE_TRANSITION_MS, reveal: 0 });
}

/** Courbe de l'iPad (CartoView.easeInOut) : t²(3 − 2t). */
export function smoothstep(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Angle en degrés de `from` à `to` par le plus court chemin, ramené à [0, 360). */
export function lerpAngle(from: number, to: number, t: number): number {
  let diff = (to - from) % 360;
  if (diff > 180) diff -= 360;
  if (diff < -180) diff += 360;
  return (((from + diff * t) % 360) + 360) % 360;
}

/** Point interpolé entre `start` (position affichée au départ ; absente : point d'arrivée) et `end`. */
export function lerpPoint(start: [number, number] | undefined, end: [number, number], t: number): [number, number] {
  if (!start || t >= 1) return end;
  return [lerp(start[0], end[0], t), lerp(start[1], end[1], t)];
}
